import React, { useEffect, useState } from 'react';
import { Plot, PlotStatus, Layout, PlotFilters } from '../../types';
import apiService from '../../services/api';
import StatusUpdateModal from '../../components/StatusUpdateModal';
import {
  Squares2X2Icon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const BookingPage: React.FC = () => {
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const [selectedLayoutId, setSelectedLayoutId] = useState<number | null>(null);
  const [plots, setPlots] = useState<Plot[]>([]);
  const [plotStatuses, setPlotStatuses] = useState<PlotStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusModalPlot, setStatusModalPlot] = useState<Plot | null>(null);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [layoutsResp, statusResp] = await Promise.all([
          apiService.getLayouts({ page: 1, limit: 100 }),
          apiService.getPlotStatuses()
        ]);
        setLayouts(layoutsResp.data.items);
        setPlotStatuses(statusResp.data.statuses);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to load data');
      }
    };
    fetchInitial();
  }, []);

  const loadPlots = async (layoutId: number) => {
    try {
      setLoading(true);
      setError(null);
      const filters: PlotFilters = { layout_id: String(layoutId), limit: 200, page: 1 };
      const response = await apiService.getPlots(filters);
      setPlots(response.data.plots);
    } catch (err: any) {
      setError(err.message || 'Failed to load plots');
    } finally {
      setLoading(false);
    }
  };

  const statusColorMap = (statusName?: string) => {
    switch (statusName) {
      case 'Open':
        return 'bg-green-500';
      case 'Hold':
        return 'bg-yellow-500';
      case 'Reserved':
        return 'bg-blue-500';
      case 'Token':
        return 'bg-purple-500';
      case 'Agreement':
        return 'bg-indigo-500';
      case 'Registered':
        return 'bg-gray-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plot Booking</h1>
          <p className="text-gray-600 mt-1">Select a layout and book plots easily</p>
        </div>
        <button onClick={() => selectedLayoutId && loadPlots(selectedLayoutId)} className="btn-outline">
          <ArrowPathIcon className="h-5 w-5 mr-1" /> Refresh
        </button>
      </div>

      <div className="card p-6">
        <label className="form-label mb-1">Choose Layout</label>
        <select
          className="form-input w-full md:w-1/2"
          value={selectedLayoutId ?? ''}
          onChange={(e) => {
            const id = parseInt(e.target.value);
            setSelectedLayoutId(id);
            loadPlots(id);
          }}
        >
          <option value="">-- Select Layout --</option>
          {layouts.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="card p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => selectedLayoutId && loadPlots(selectedLayoutId)} className="btn-primary">Retry</button>
        </div>
      )}

      {/* Legend */}
      {plotStatuses.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {plotStatuses.map((status) => (
            <div key={status.id} className="flex items-center space-x-2 text-sm">
              <span className={`h-4 w-4 rounded ${statusColorMap(status.status_name)}`}></span>
              <span>{status.status_name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Plot Grid */}
      {loading ? (
        <div className="card p-6 flex items-center justify-center min-h-[200px]">Loading...</div>
      ) : (
        selectedLayoutId && (
          <div className="card p-4">
            {plots.length === 0 ? (
              <div className="text-center py-12">
                <Squares2X2Icon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No plots available for this layout.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {plots.map((plot) => (
                  <button
                    key={plot.id}
                    onClick={() => setStatusModalPlot(plot)}
                    className={
                      `${statusColorMap(plot.status_name)} text-white rounded py-2 text-xs font-medium focus:outline-none hover:opacity-90`
                    }
                    title={`Plot ${plot.plot_number}`}
                  >
                    {plot.plot_number}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      )}

      {statusModalPlot && (
        <StatusUpdateModal
          plot={statusModalPlot}
          statuses={plotStatuses}
          onClose={() => setStatusModalPlot(null)}
          onUpdated={(updated) => {
            setPlots(plots.map(p => p.id === updated.id ? { ...p, ...updated } : p));
          }}
        />
      )}
    </div>
  );
};

export default BookingPage; 